import AddIcon from '@mui/icons-material/Add';
import CommentIcon from '@mui/icons-material/Comment';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import RemoveIcon from '@mui/icons-material/Remove';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import highchartsWindbarb from 'highcharts/modules/windbarb';
import ZoomModule from 'highcharts/modules/drag-panes';
import Draggable from 'highcharts/modules/draggable-points';
import './config';
import { useSnackbar } from 'notistack';
import React, {
  useEffect, useRef, useState,
} from 'react';
import { usePhenologicalCycleBands, phenoCycleBandsToHighcharts } from '../../hooks/usePhenologicalCycleBands';
import commentIcon from '../../assets/svg/graphComment.svg';
import { useBiweeklyReportContext } from '../../context/BiweeklyReportContext';
import { useChartRulerContext } from '../../context/ChartRulerContext';
import {
  ExceptionRange,
  useChartExceptionContext,
} from '../../context/ChartExceptionContext';
import { useDashboardDownloadContext } from '../../context/DashboardDownloadContext';
import apiClient from '../../requests/api/apiClient';
import { ConversationSchema, TimePointSchema } from '../../requests/api/apiTypes';
import { Interval } from '../../types';
import useStyles from './styles';
import {
  ChartInstance,
  ChartPoint,
  ChartSeries,
  ScatterPoint,
  YAxisLimits,
} from './types';
import {
  CHART_TITLE_HORAS_FRIO_ACUMULADAS,
} from '../../constants/graphs';
import { RULER_LINES } from './constants';
import { buildRulerBadgeHTML, buildRulerPlotLine } from './utils/chartRuler';
import {
  buildCsvDownloader,
  checkExistingConversation,
  getPointExceptionBucket,
  getPointWidth,
  // getGranularity,
  getScatterConversations,
  getTimePoint,
} from './utils';
import { calculateYAxisLimits } from './LineCharts/utils';

dayjs.extend(utc);

// Initialize the modules
Draggable(Highcharts);
ZoomModule(Highcharts);
highchartsMore(Highcharts);
highchartsWindbarb(Highcharts);

const DEFAULT_Y_MARGIN_PERCENT = 0.4;
const ONE_DAY_MS = 24 * 3600 * 1000;

const getVisibleDataInRange = (
  chart: ChartInstance,
  minDate: number,
  maxDate: number,
  marginPercent: number = DEFAULT_Y_MARGIN_PERCENT,
): YAxisLimits | null => {
  const visibleSeries = chart.series.filter(
    (s: ChartSeries) => s.visible && s.type === 'line',
  );
  let minY = Infinity;
  let maxY = -Infinity;

  visibleSeries.forEach((seriesItem: ChartSeries) => {
    if (seriesItem.data && seriesItem.data.length > 0) {
      seriesItem.data.forEach((point: ChartPoint) => {
        const pointX = point.x || point.category;
        const isInRange = pointX !== undefined && pointX >= minDate && pointX <= maxDate;
        const isValidY = typeof point.y === 'number' && !Number.isNaN(point.y);
        if (isInRange && isValidY && point.y !== undefined) {
          minY = Math.min(minY, point.y);
          maxY = Math.max(maxY, point.y);
        }
      });
    }
  });

  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) {
    return null;
  }

  const range = maxY - minY;
  const margin = range * marginPercent;
  return {
    min: minY === 0 ? 0 : minY - margin,
    max: maxY + margin,
  };
};

const applyChartZoom = (chart: ChartInstance, minDate: number, maxDate: number): void => {
  if (!chart || !chart.userOptions.series.length || !chart.userOptions.plotOptions.zoomSync) {
    return;
  }

  chart.xAxis[0].setExtremes(minDate, maxDate);

  const pointWidth = getPointWidth(minDate, maxDate);
  chart.series.forEach((s: ChartSeries) => {
    if (s.type === 'column') {
      (s as any).update({ pointWidth });
    }
  });
};

const resetChartZoom = (chart: ChartInstance, interval: Interval): void => {
  if (!chart || !chart.userOptions.series.length || !chart.userOptions.plotOptions.zoomSync) {
    return;
  }

  const startDateUnix = dayjs(interval.startDate).valueOf();
  const endDateUnix = dayjs(interval.endDate).valueOf();

  chart.xAxis[0].setExtremes(startDateUnix, endDateUnix);

  if (chart.userOptions.plotOptions.autoAdjustYAxis) {
    const originalYAxisLimits = getVisibleDataInRange(chart, startDateUnix, endDateUnix);
    if (originalYAxisLimits && chart.yAxis.length > 0) {
      const dataSeries = chart.series.filter(
        (s: ChartSeries) => s.visible
          && s.type !== 'scatter' && s.type !== 'arearange',
      );
      if (dataSeries.length) {
        const yLimits = calculateYAxisLimits(dataSeries);
        if (!(yLimits.min === 0 && yLimits.max === 1)) {
          const range = yLimits.max - yLimits.min;
          const mg = range * 0.05 || 1;
          chart.yAxis[0].setExtremes(
            Math.max(0, yLimits.min - mg * 3),
            yLimits.max + mg,
          );
        }
      }
    }
  }

  const pointWidth = getPointWidth(interval.startDate, interval.endDate);
  chart.series.forEach((s: ChartSeries) => {
    if (s.type === 'column') {
      (s as any).update({ pointWidth });
    }
  });
};

type ChartViewType = 'settings' | 'dashboard' | 'graphs' | 'fullScreen';

type ChartProps = {
  chartView: ChartViewType;
  filter: React.ReactNode | (('dashboard' | 'graphs') extends never ? never : undefined);
} & BaseChartProps;

interface BaseChartProps {
  interval: Interval | null;
  chartView: ChartViewType;
  chartType?: string,
  title?: string,
  series?: Array<Highcharts.SeriesOptionsType>,
  categories?: Array<string>,
  yAxisTitle?: string,
  xAxisTitle?: string,
  colors?: Array<string>,
  adjustYWithLegend?: boolean,
  legend?: Highcharts.LegendOptions,
  tooltip?: Highcharts.TooltipOptions,
  chartConfig?: any,
  showFullScreen?: boolean,
  showMarkers?: boolean,
  xPlotBands?: Array<object>,
  yPlotBands?: Array<object>,
  hideYValues?: boolean,
  hideXValues?: boolean,
  button?: React.ReactNode,
  onConfigClick?: () => void,
  onFullScreenClick?: () => void,
  setModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean,
    conversationId: string,
    timePointData: TimePointSchema,
  }>>,
  conversations?: Array<ConversationSchema>,
  absoluteCategories?: string[],
  height?: number | string,
  setHideDepthsText?: React.Dispatch<React.SetStateAction<boolean>>,
  stackButton?: {
    onClick: () => void;
    isStacked: boolean;
    component?: React.ReactNode;
  },
  zoomSync?: boolean;
  autoAdjustYAxis?: boolean;
  sectorId?: string;
  graphName?: string;
}

function BaseHighchart({
  chartView,
  chartType = 'line',
  title = '',
  series = [],
  categories = [],
  yAxisTitle = '',
  xAxisTitle = '',
  interval,
  colors = ['#536e7b', '#35a0d7', '#77d863', '#f7a35c', '#8085e9', '#f15c80', '#FEBF23', '#2b908f', '#f45b5b', '#91e8e1'],
  legend = { enabled: true },
  tooltip = {},
  chartConfig = {}, // Configuraciones personalizadas adicionales
  showFullScreen = true,
  adjustYWithLegend = false,
  showMarkers = false,
  xPlotBands = [],
  yPlotBands = [],
  hideYValues = false,
  hideXValues = false,
  button = null,
  filter,
  onConfigClick,
  onFullScreenClick = () => { },
  setModalState,
  conversations,
  absoluteCategories,
  height = 300,
  setHideDepthsText,
  stackButton,
  zoomSync = false,
  autoAdjustYAxis = false,
  sectorId,
  graphName,
  ...props
}: ChartProps) {
  const classes = useStyles();
  const phenologicalCycleBands = usePhenologicalCycleBands(sectorId);
  const phenoCyclesConfig = phenoCycleBandsToHighcharts(phenologicalCycleBands);
  const [expanded, setExpanded] = useState(true);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [isCommentModeActive, setIsCommentModeActive] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isBiweeklyReportView } = useBiweeklyReportContext();
  const chartRulerContext = useChartRulerContext();
  const chartExceptionContext = useChartExceptionContext();
  const dashboardDownloadContext = useDashboardDownloadContext();
  const [localExceptionRanges, setLocalExceptionRanges] = useState<
  ExceptionRange[]
  >([]);

  useEffect(() => {
    if (chartExceptionContext || !sectorId) return;
    apiClient.exceptionRanges.get(sectorId).then((res: any) => {
      setLocalExceptionRanges(res.data?.exceptionRanges || []);
    }).catch(() => { });
  }, [sectorId, chartExceptionContext]);

  const effectiveExceptionRanges = chartExceptionContext?.exceptionRanges || localExceptionRanges;

  const exceptionPlotBands = (effectiveExceptionRanges || [])
    .filter((r) => !r.chart)
    .map((r) => ({
      from: dayjs.utc(r.startDate).valueOf(),
      to: dayjs.utc(r.endDate).valueOf(),
      color: 'rgba(160, 160, 160, 0.2)',
      zIndex: 5,
      inFront: true,
    }));
  const pendingExceptionBands = (chartExceptionContext?.pendingRanges || []).map((r) => ({
    from: Math.min(r[0], r[1]),
    to: Math.max(r[0], r[1]),
    color: 'rgba(255, 140, 0, 0.2)',
    zIndex: 6,
    inFront: true,
  }));

  useEffect(() => {
    if (
      !isBiweeklyReportView
      && interval
      && dashboardDownloadContext
    ) {
      const chartId = `chart_${title.replace(/[^a-zA-Z0-9-_]+/g, '_')}`;
      const getExportData = () => {
        const chart = chartRef.current?.chart as any;
        if (!chart || !interval) return null;
        return {
          chart,
          categories,
          title,
          startDate: interval.startDate,
          endDate: interval.endDate,
        };
      };
      dashboardDownloadContext.registerChart(chartId, getExportData);
      return () => dashboardDownloadContext.unregisterChart(chartId);
    }
    return undefined;
  }, [
    isBiweeklyReportView,
    interval,
    dashboardDownloadContext,
    title,
    categories,
  ]);

  useEffect(() => {
    if (!chartRulerContext || isBiweeklyReportView) return undefined;
    const chart = chartRef.current?.chart as any;
    if (!chart?.xAxis?.[0]) return undefined;

    // Redraws the rulers on every chart `redraw` (stacked/original toggle, zoom)
    // so they persist; plot lines added imperatively are lost on those updates.
    const draw = () => {
      if (!chart?.xAxis?.[0]) return;
      const rulerValues = chartRulerContext.rulerPoints;
      const [firstRulerValue] = rulerValues;
      RULER_LINES.forEach((line, index) => {
        chart.xAxis[0].removePlotLine(line.id);
        const value = rulerValues[index];
        if (value === null) return;
        const compareToX = index > 0 ? firstRulerValue : null;
        const badgeHTML = buildRulerBadgeHTML(chart, value, line.label, line.color, compareToX);
        chart.xAxis[0].addPlotLine(buildRulerPlotLine(line, value, badgeHTML));
      });
    };

    draw();
    const removeRedrawListener = Highcharts.addEvent(chart, 'redraw', draw);
    return () => removeRedrawListener();
  }, [chartRulerContext?.rulerPoints, isBiweeklyReportView, chartRulerContext, series]);

  const handleCommentButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCommentModeActive(!isCommentModeActive);
  };

  const handleFullScreenClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFullScreenClick();
  };

  const granularity = 'daily'; // getGranularity(startDate, endDate);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadModalOpen(true);
  };

  const handleDownloadFormat = (format: 'csv' | 'excel') => {
    const chart = chartRef.current?.chart as any;
    if (!interval) return;
    buildCsvDownloader(chart, categories, title, interval.startDate, interval.endDate, format);
    setDownloadModalOpen(false);
  };

  const handleCloseDownloadModal = () => {
    setDownloadModalOpen(false);
  };

  const renderCommentMarkers = () => {
    if (!conversations || !absoluteCategories?.length || isBiweeklyReportView) return [];
    const yAxis = chartRef.current?.chart?.yAxis?.[0];
    return [{
      type: 'scatter',
      name: 'Comentarios',
      className: 'cdtec-chart-comments',
      custom: { excludeFromExport: true },
      data: getScatterConversations(
        conversations,
        granularity,
        absoluteCategories,
        { min: yAxis?.min ?? 0, max: yAxis?.max ?? 1 },
      ) as ScatterPoint[],
      showInLegend: false,
      marker: {
        symbol: `url(${commentIcon})`,
        width: 30,
        height: 30,
      },
      stickyTracking: false,
      events: {
        click(e: Highcharts.PointClickEventObject) {
          const point = e.point as ScatterPoint;
          if (setModalState && point.custom) {
            setModalState({
              open: true,
              conversationId: point.custom.conversationId,
              timePointData: point.custom.timePointData,
            });
          }
        },
      },
    }];
  };

  const defaultFontStyles = {
    fontSize: '11px',
    fontWeight: 'normal',
    color: '#385576',
    fontFamily: "'Roboto', sans-serif",
    letterSpacing: '0.25px',
  };

  const getDashboardSpacingTop = () => -10;

  const getSpacingTop = () => {
    if (chartView === 'dashboard') return legend.enabled ? getDashboardSpacingTop() : 20;
    return legend.enabled ? 10 : 20;
  };
  let effectiveHeight = height;
  let effectiveLegend = legend;
  if (isBiweeklyReportView) {
    effectiveLegend = { enabled: false };

    if (
      title === CHART_TITLE_HORAS_FRIO_ACUMULADAS
      || title.includes('Grado')
    ) {
      effectiveHeight = 100;
    }
  }

  const getLegendMarginBottom = () => 0;

  const chartOptions = {
    chart: {
      type: chartType,
      alignTicks: true,
      height: effectiveHeight,
      spacingTop: getSpacingTop(),
      spacingBottom: chartView === 'dashboard' ? 5 : 10,
      spacingLeft: chartView === 'dashboard' ? 2 : 4,
      spacingRight: chartView === 'dashboard' ? 2 : 6,
      marginLeft: chartView === 'dashboard' ? 50 : 36,
      marginRight: chartView === 'dashboard' ? 10 : undefined,
      zoomType: 'xy',
      panning: true,
      panKey: 'shift',
      events: {
        selection(e: any) {
          if (chartExceptionContext?.isExceptionToolActive) {
            const startTs = e.xAxis?.[0]?.min;
            const endTs = e.xAxis?.[0]?.max;
            if (startTs != null && endTs != null) {
              chartExceptionContext.addPendingRange([startTs, endTs]);
            }
            e.preventDefault();
            return;
          }
          if (!zoomSync || !interval) return;
          try {
            if (e.resetSelection !== true) {
              const minDate = dayjs(e.xAxis[0].min).valueOf();
              const maxDate = dayjs(e.xAxis[0].max).valueOf();
              e.target.yAxis[0].setExtremes(e.yAxis[0].min, e.yAxis[0].max);
              Highcharts.charts.forEach((chart) => {
                if (chart) {
                  applyChartZoom(chart as ChartInstance, minDate, maxDate);
                }
              });
              e.target.showResetZoom();
              e.preventDefault();
            } else {
              Highcharts.charts.forEach((chart) => {
                if (chart) {
                  resetChartZoom(chart as ChartInstance, interval);
                }
              });
            }
          } catch (error) {
            console.error('Error en el evento selection', error);
          }
        },
        click(this: Highcharts.Chart, e: Highcharts.ChartClickEventObject) {
          // Click para colocar regla
          if (chartRulerContext?.isRulerActive && chartRulerContext) {
            const xValue = e.xAxis?.[0]?.value ?? (this.xAxis?.[0]
              ? this.xAxis[0].toValue(e.chartX) : null);
            if (xValue != null && !Number.isNaN(xValue)) {
              chartRulerContext.addRulerPoint(xValue);
            }
            return;
          }
          // Click para colocar comentario donde sea (no requiere caer en un punto).
          // Captura tanto X (timestamp) como Y (valor) para que el marcador aparezca
          // exactamente donde se hizo click.
          if (isCommentModeActive && setModalState) {
            const xValue = e.xAxis?.[0]?.value ?? (this.xAxis?.[0]
              ? this.xAxis[0].toValue(e.chartX) : null);
            const yValue = e.yAxis?.[0]?.value ?? (this.yAxis?.[0]
              ? this.yAxis[0].toValue(e.chartY) : null);
            if (xValue == null || Number.isNaN(xValue)) return;
            const safeY = typeof yValue === 'number' && Number.isFinite(yValue) ? yValue : undefined;
            const timePoint = getTimePoint(xValue, safeY);
            const hasExistingConversation = checkExistingConversation(timePoint, conversations);
            if (hasExistingConversation) {
              enqueueSnackbar('Ya existe un comentario en este punto', { variant: 'warning' });
            } else {
              setModalState({ open: true, conversationId: '', timePointData: timePoint });
            }
            setIsCommentModeActive(false);
          }
        },
      },
      resetZoomButton: {
        position: {
          align: 'right',
          verticalAlign: 'top',
          x: -10,
          y: 10,
        },
        theme: {
          fill: '#f7f7f7',
          stroke: '#cccccc',
          r: 4,
          states: {
            hover: {
              fill: '#e6e6e6',
              stroke: '#cccccc',
              style: {
                color: '#333333',
              },
            },
          },
        },
      },
    },
    title: {
      text: '',
    },
    xAxis: {
      categories,
      title: {
        text: xAxisTitle,
        style: {
          ...defaultFontStyles,
        },
      },
      alignTicks: true,
      tickmarkPlacement: 'on',
      gridLineWidth: 1,
      gridLineColor: '#E0E0E0',
      gridLineDashStyle: 'Dot',
      labels: {
        rotation: -10,
        enabled: !hideXValues,
        style: {
          color: '#385576',
        },
      },
      plotBands: [
        ...phenoCyclesConfig.plotBands,
        ...(xPlotBands?.map((band: any) => ({
          from: band.from,
          to: band.to,
          color: band.color,
          label: {
            verticalAlign: 'bottom',
            align: 'right',
            y: -2,
            x: -10,
            useHTML: true,
            style: {
              color: '#000',
              fontSize: '11px',
            },
          },
        })) || []),
        ...exceptionPlotBands,
        ...pendingExceptionBands,
      ],
      plotLines: phenoCyclesConfig.plotLines,
    },
    yAxis: {
      startOnTick: false,
      endOnTick: false,
      title: {
        text: yAxisTitle || null,
        style: {
          ...defaultFontStyles,
          fontSize: '7px',
          fontWeight: '600',
        },
        margin: chartView === 'dashboard' ? 2 : 4,
      },
      labels: {
        enabled: !hideYValues,
        style: {
          color: '#385576',
          fontSize: '9px',
        },
        x: -2,
      },
      plotBands: yPlotBands?.map((band: any) => ({
        from: band.from,
        to: band.to,
        color: band.color,
        label: {
          text: band.name || '',
          verticalAlign: 'bottom',
          align: 'right',
          y: -2,
          x: -10,
          useHTML: true,
          style: {
            color: '#000',
            fontSize: '11px',
          },
        },
      })),
    },
    legend: {
      ...effectiveLegend,
      itemStyle: {
        ...defaultFontStyles,
      },
      verticalAlign: 'top',
      layout: 'horizontal',
      align: 'left',
      itemDistance: chartView === 'dashboard' ? 10 : 20,
      itemMarginBottom: chartView === 'dashboard' ? getLegendMarginBottom() : 36,
      x: 10,
    },
    tooltip: {
      ...tooltip,
      outside: true,
      style: {
        ...defaultFontStyles,
        opacity: 0.8,
      },
      positioner(this: Highcharts.TooltipPositionerPointObject,
        labelWidth: number,
        labelHeight: number,
        point: Highcharts.Point) {
        const TOOLTIP_CURSOR_GAP = 12;
        const TOOLTIP_EDGE_MARGIN = 4;
        const TOOLTIP_OVERFLOW_MARGIN = 6;
        const TOOLTIP_TOP_OFFSET = 8;

        const { chart } = this as unknown as { chart: Highcharts.Chart };
        const cursorX = (point.plotX ?? 0) + (chart.plotLeft || 0);
        let x = cursorX + TOOLTIP_CURSOR_GAP;
        if (x + labelWidth > chart.chartWidth - TOOLTIP_OVERFLOW_MARGIN) {
          x = cursorX - labelWidth - TOOLTIP_CURSOR_GAP;
        }
        if (x < TOOLTIP_EDGE_MARGIN) x = TOOLTIP_EDGE_MARGIN;
        if (x + labelWidth > chart.chartWidth - TOOLTIP_EDGE_MARGIN) {
          x = chart.chartWidth - labelWidth - TOOLTIP_EDGE_MARGIN;
        }
        return { x, y: TOOLTIP_TOP_OFFSET };
      },
      valueDecimals: 2,
      shared: true,
    },
    colors,
    series: [
      ...series.map((s: any) => ({
        ...s,
        zIndex: s.zIndex
          ?? (s.type === 'column' || s.type === 'bar' ? 1 : 2),
      })),
      ...renderCommentMarkers(),
    ],
    credits: {
      enabled: false,
    },
    plotOptions: {
      scatter: {
        marker: {
          enabled: true,
          zIndex: 1000,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
      bar: {
        centerInCategory: true,
      },
      line: {
        lineWidth: 2,
        marker: {
          enabled: showMarkers,
        },
      },
      arearange: {
        dataLabels: {
          enabled: true,
          formatter(this: any): any {
            if (this.key === 'highPoint') {
              return `${this.series.name} (${this.y})`;
            }
            return '';
          },
          style: {
            color: '#515151',
            fontSize: '12px',
            fontWeight: '350',
          },
          verticalAlign: 'bottom',
          align: 'right',
          y: -2,
          x: -10,
          useHTML: true,
        },
      },
      column: {
        stacking: 'normal',
      },
      series: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 5,
            },
          },
        },
        states: {
          hover: {
            lineWidthPlus: 1,
          },
        },
      },
    },
    lang: {
      noData: 'No hay datos para mostrar',
    },
    noData: {
      style: {
        fontSize: '18px',
        fontWeight: '400px',
      },
    },
  };

  const options = {
    ...chartOptions,
    ...chartConfig,
    ...props,
    plotOptions: {
      zoomSync,
      autoAdjustYAxis,
      ...chartOptions.plotOptions,
      series: {
        ...chartOptions.plotOptions.series,
        point: {
          events: {
            click(this: Highcharts.Point, e: Highcharts.PointerEventObject) {
              if (chartExceptionContext?.isExceptionToolActive && sectorId) {
                e.stopPropagation();
                if (!graphName) {
                  enqueueSnackbar('Este gráfico no admite excluir puntos', { variant: 'warning' });
                  return;
                }
                if (typeof this.x !== 'number') return;
                const seriesXs: number[] = (((this.series as any)?.points || []) as any[])
                  .map((p: any) => p.x)
                  .filter((v: any): v is number => typeof v === 'number');
                const { startDate, endDate } = getPointExceptionBucket(seriesXs, this.x);
                const current = chartExceptionContext.exceptionRanges || [];
                const alreadyExists = current.some(
                  (r) => r.chart === graphName
                    && r.startDate === startDate && r.endDate === endDate,
                );
                if (alreadyExists) return;
                const updated = [...current, { startDate, endDate, chart: graphName }];
                chartExceptionContext.persistExceptionRanges(sectorId, updated)
                  .then(() => {
                    enqueueSnackbar('Punto excluido del gráfico', { variant: 'success' });
                  })
                  .catch(() => {
                    enqueueSnackbar('No se pudo guardar la excepción', { variant: 'error' });
                  });
                return;
              }
              if (isCommentModeActive) {
                const timePoint = getTimePoint(this.x);

                if (setModalState && timePoint) {
                  const hasExistingConversation = checkExistingConversation(
                    timePoint,
                    conversations,
                  );

                  if (!hasExistingConversation) {
                    setModalState({ open: true, conversationId: '', timePointData: timePoint });
                  } else {
                    enqueueSnackbar('Ya existe un comentario en este punto', { variant: 'warning' });
                  }
                }

                setIsCommentModeActive(false);
                e.stopPropagation();
              }
            },
          },
        },
        events: {
          legendItemClick(this: Highcharts.Series) {
            if (!adjustYWithLegend) return;
            const { chart } = this;
            setTimeout(() => {
              const visibleSeries = chart.series.filter((s: Highcharts.Series) => s.visible && s.type === 'line');

              const limits = calculateYAxisLimits(visibleSeries);
              if (chart.yAxis.length === 1) {
                chart.yAxis[0].setExtremes(limits.min, limits.max);
              }
            }, 0);
          },
        },
      },
    },
    chart: {
      ...chartOptions.chart,
      ...(chartConfig?.chart || {}),
    },
    xAxis: (() => {
      const baseAxis: any = (chartOptions as any).xAxis || {};
      const cfgAxis: any = (chartConfig as any)?.xAxis || {};
      // eslint-disable-next-line react/prop-types
      const propsAxis: any = (props as any)?.xAxis || {};
      const finalType = (
        propsAxis.type ?? cfgAxis.type ?? baseAxis.type
      );
      const isDatetime = finalType === 'datetime';
      const merged: any = {
        ...baseAxis,
        ...cfgAxis,
        ...propsAxis,
      };

      const hasCategories = Array.isArray(cfgAxis.categories) && cfgAxis.categories.length > 0;
      if (isDatetime && !hasCategories) {
        delete merged.categories;
        merged.ordinal = false;
        if (merged.tickInterval === undefined) merged.tickInterval = ONE_DAY_MS;
        if (merged.minPadding === undefined) merged.minPadding = 0;
        if (merged.maxPadding === undefined) merged.maxPadding = 0;
        // Don't extend visible range to next tick — keeps min/max exact (avoids extra day visually)
        merged.startOnTick = false;
        merged.endOnTick = false;
        // Force ALL line charts to share the SAME horizontal domain from the global interval.
        if (interval) {
          merged.min = dayjs(interval.startDate).utc().startOf('day').valueOf();
          merged.max = dayjs(interval.endDate).add(dayjs().utcOffset(), 'minutes').valueOf();
        }
      }
      const applyPheno = isDatetime && !hasCategories;
      merged.plotBands = [
        ...(applyPheno ? phenoCyclesConfig.plotBands : []),
        ...(baseAxis.plotBands || []),
        ...(cfgAxis.plotBands || []),
        ...(propsAxis.plotBands || []),
      ];
      merged.plotLines = [
        ...(applyPheno ? phenoCyclesConfig.plotLines : []),
        ...(baseAxis.plotLines || []),
        ...(cfgAxis.plotLines || []),
        ...(propsAxis.plotLines || []),
      ];
      return merged;
    })(),
  };

  // Las transiciones fenológicas ahora se inyectan vía phenoCyclesConfig (plotBands + plotLines)
  // unificadas con el resto de la app — ver usePhenologicalCycleBands.

  return (
    <>
      <Dialog open={downloadModalOpen} onClose={handleCloseDownloadModal}>
        <DialogTitle>Seleccionar formato de descarga</DialogTitle>
        <DialogContent>
          <Typography>¿En qué formato deseas descargar los datos del gráfico?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDownloadModal} color="primary">
            Cancelar
          </Button>
          <Button onClick={() => handleDownloadFormat('csv')} color="primary" variant="outlined">
            CSV
          </Button>
          <Button onClick={() => handleDownloadFormat('excel')} color="primary" variant="contained">
            Excel
          </Button>
        </DialogActions>
      </Dialog>
      {chartView === 'dashboard' ? (
        <div className={classes.dashboardChartContainer}>
          <Box className={expanded
            ? classes.dashboardChartTitleContainer : classes.dashboardChartTitleContainerClosed}
          >
            <Typography className={expanded
              ? classes.dashboardChartTitle
              : classes.dashboardChartTitleClosed} variant="h4" color="textPrimary">
              {title}
            </Typography>
          </Box>
          <Accordion className={classes.dashboardAccordion}
            expanded={expanded}
            onChange={() => {
              if (setHideDepthsText) {
                setHideDepthsText(expanded);
              }
              setExpanded(!expanded);
            }}
            elevation={0}
            sx={{
              '& .Mui-expanded': {
                minHeight: '0px !important',
              },
            }}
          >
            <AccordionSummary className={classes.accordionSummary}
              sx={{
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '0px !important',
                },
              }}
            >
              <Box
                className={classes.iconButtonsContainerDashboard}
                onClick={(e) => e.stopPropagation()}
              >
                {stackButton && (
                  stackButton.component || (
                    <Button
                      onClick={(e) => { e.stopPropagation(); stackButton.onClick(); }}
                      size="small"
                      variant="outlined"
                      style={{
                        backgroundColor: '#3693d5',
                        color: 'white',
                        border: '1px solid #3693d5',
                        marginRight: '8px',
                        textTransform: 'none',
                        padding: '4px 12px',
                      }}
                    >
                      {stackButton.isStacked ? 'Apilado' : 'Valores Originales'}
                    </Button>
                  )
                )}
                {!isBiweeklyReportView && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDownloadClick(e); }}
                    sx={{ padding: '4px' }}
                  >
                    <FileDownloadIcon sx={{ color: '#385576', fontSize: '18px' }} />
                  </IconButton>
                )}
                {setModalState && !isBiweeklyReportView && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleCommentButtonClick(e); }}
                    sx={{ padding: '4px' }}
                  >
                    <CommentIcon sx={{
                      color: isCommentModeActive ? '#3eab59' : 'primary.main',
                      fontSize: '18px',
                    }} />
                  </IconButton>
                )}
                {onConfigClick && !isBiweeklyReportView && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onConfigClick(); }}
                    sx={{ padding: '4px' }}
                  >
                    <SettingsIcon sx={{ color: '#385576', fontSize: '18px' }} />
                  </IconButton>
                )}
                {showFullScreen && !isBiweeklyReportView && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleFullScreenClick(e); }}
                    sx={{ padding: '4px' }}
                  >
                    <FullscreenIcon sx={{ color: '#385576', fontSize: '18px' }} />
                  </IconButton>
                )}
                {!isBiweeklyReportView && (
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                    sx={{ padding: '4px' }}
                  >
                    {expanded ? (
                      <RemoveIcon sx={{ color: '#385576', fontSize: '18px' }} />
                    ) : (
                      <AddIcon sx={{ color: '#385576', fontSize: '18px' }} />
                    )}
                  </IconButton>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.dashboardAccordionDetails}>
              <Box
                sx={{
                  cursor: (isCommentModeActive || chartRulerContext?.isRulerActive) ? 'crosshair' : 'default',
                  width: '100%',
                }}
              >
                {filter}
                <HighchartsReact
                  className={classes.dashboardChart}
                  ref={chartRef}
                  highcharts={Highcharts}
                  options={options}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </div>
      ) : (
        <Card className={classes.chartCard}>
          <CardContent>
            {chartView === 'graphs' || chartView === 'settings' ? (
              <Accordion
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
                elevation={0}
              >
                <AccordionSummary className={classes.accordionSummary}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      margin: 0,
                    },
                  }}
                >
                  <Box className={classes.titleContainer}>
                    <Typography variant="h4" color="primary">
                      {title}
                    </Typography>
                  </Box>
                  <Box className={classes.iconButtonsContainer}>
                    {stackButton && (
                      stackButton.component || (
                        <Button
                          onClick={stackButton.onClick}
                          size="small"
                          variant="outlined"
                          style={{
                            backgroundColor: '#3693d5',
                            color: 'white',
                            border: '1px solid #3693d5',
                            marginRight: '8px',
                            textTransform: 'none',
                            padding: '4px 12px',
                          }}
                        >
                          {stackButton.isStacked ? 'Apilado' : 'Valores Originales'}
                        </Button>
                      )
                    )}
                    {!isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={handleDownloadClick}
                        sx={{ padding: '4px' }}
                      >
                        <FileDownloadIcon sx={{ color: '#385576', fontSize: '18px' }} />
                      </IconButton>
                    )}
                    {setModalState && !isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={handleCommentButtonClick}
                        sx={{ padding: '4px' }}
                      >
                        <CommentIcon sx={{
                          color: isCommentModeActive ? '#3eab59' : 'primary.main',
                          fontSize: '18px',
                        }} />
                      </IconButton>
                    )}
                    {onConfigClick && chartView !== 'settings' && !isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={onConfigClick}
                        sx={{ padding: '4px' }}
                      >
                        <SettingsIcon sx={{ color: '#385576', fontSize: '18px' }} />
                      </IconButton>
                    )}
                    {showFullScreen && !isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={handleFullScreenClick}
                        sx={{ padding: '4px' }}
                      >
                        <FullscreenIcon sx={{ color: '#385576', fontSize: '18px' }} />
                      </IconButton>
                    )}
                    {!isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        sx={{ padding: '4px' }}
                      >
                        {expanded ? (
                          <RemoveIcon sx={{ color: '#385576', fontSize: '18px' }} />
                        ) : (
                          <AddIcon sx={{ color: '#385576', fontSize: '18px' }} />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  {button && <Box>{button}</Box>}
                  {filter}
                  <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={{
                      ...options,
                      chart: {
                        ...(options?.chart || {}),
                        height: chartView === 'settings' || chartView === 'graphs' ? height : '50%',
                      },
                    }}
                  />
                </AccordionDetails>
              </Accordion>
            ) : (
              // chartView === 'fullScreen': barra de acciones + chart ocupando
              // todo el alto disponible
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    borderBottom: '1px solid #E2E8F0',
                    mb: 1,
                  }}
                >
                  <Box className={classes.titleContainer}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A' }}>
                      {title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {stackButton && (
                      stackButton.component || (
                        <Button
                          onClick={stackButton.onClick}
                          size="small"
                          variant="outlined"
                          style={{
                            backgroundColor: '#3693d5',
                            color: 'white',
                            border: '1px solid #3693d5',
                            marginRight: '8px',
                            textTransform: 'none',
                            padding: '4px 12px',
                          }}
                        >
                          {stackButton.isStacked ? 'Apilado' : 'Valores Originales'}
                        </Button>
                      )
                    )}
                    {!isBiweeklyReportView && (
                      <IconButton size="small" onClick={handleDownloadClick} title="Descargar datos">
                        <FileDownloadIcon sx={{ color: '#385576', fontSize: '20px' }} />
                      </IconButton>
                    )}
                    {setModalState && !isBiweeklyReportView && (
                      <IconButton
                        size="small"
                        onClick={handleCommentButtonClick}
                        title="Modo comentario"
                      >
                        <CommentIcon sx={{
                          color: isCommentModeActive ? '#3eab59' : '#385576',
                          fontSize: '20px',
                        }} />
                      </IconButton>
                    )}
                    {onConfigClick && !isBiweeklyReportView && (
                      <IconButton size="small" onClick={onConfigClick} title="Configuración del gráfico">
                        <SettingsIcon sx={{ color: '#385576', fontSize: '20px' }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {filter}
                <Box sx={{
                  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
                }}>
                  <HighchartsReact
                    ref={chartRef}
                    highcharts={Highcharts}
                    options={{
                      ...options,
                      chart: {
                        ...(options?.chart || {}),
                        height,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default BaseHighchart;
